/* eslint-disable react/require-default-props */
import React, {
  useState, useCallback, useContext, useRef, useEffect
} from 'react';
import proptypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import classNames from 'classnames/bind';
import { VideoPlayButton } from './VideoPlayButton';
import './video-hero-image.scss';

const { string, bool } = proptypes;

const VideoHeroImage = React.memo(({
  imageUrl = '',
  altText = '',
  fullWidth = false,
  brandHero = false,
  videoClasses = '',
  isBentoHero = false,
  desktopVideoUrl = '',
  mobileVideoUrl = '',
  mobilePosterImageUrl = null,
  desktopPosterImageUrl = null,
  shouldLoopVideo = true,
  videoId = 'uniqueVideoId'
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showReplay, setShowReplay] = useState(false);

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const [currentVideoSources, setCurrentVideoSources] = useState({
    imageUrl,
    desktopVideoUrl,
    mobileVideoUrl,
  });

  useEffect(() => {
    const isChanged = currentVideoSources.imageUrl !== imageUrl
      || currentVideoSources.desktopVideoUrl !== desktopVideoUrl
      || currentVideoSources.mobileVideoUrl !== mobileVideoUrl;

    if (isChanged) {
      setCurrentVideoSources({
        imageUrl,
        desktopVideoUrl,
        mobileVideoUrl,
      });
    }
  }, [imageUrl, desktopVideoUrl, mobileVideoUrl]);

  const handlePlayPause = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsPlaying((prev) => {
      if (prev) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      return !prev;
    });
    if (showReplay) setShowReplay(false);
  }, [showReplay]);

  const handleVideoEnded = () => {
    if (!shouldLoopVideo) {
      setShowReplay(true);
      setIsPlaying(false);
    }
  };

  const containerClasses = classNames(
    'video-hero-image sui-overflow-hidden sui-relative',
    {
      'sui-w-full': fullWidth,
      'sui-h-full': isBentoHero,
      'sui-row-start-1 md:sui-row-start-auto': brandHero,
    }
  );

  const sources = isBentoHero
    ? [
      <source
        key={`${videoId}-desktop`}
        className="sui-w-full sui-top-0 sui-left-0"
        data-testid="video-hero-image-bento-source-desktop"
        src={currentVideoSources.desktopVideoUrl}
        type="video/mp4"
        media="(min-width: 1024px)"
        alt={altText}
      />,
      <source
        key={`${videoId}-mobile`}
        className="sui-w-full sui-top-0 sui-left-0"
        data-testid="video-hero-image-bento-source-mobile"
        src={currentVideoSources.mobileVideoUrl}
        type="video/mp4"
        alt={altText}
      />
    ]
    : [
      <source
        key={`${videoId}-main`}
        src={currentVideoSources.imageUrl}
        type="video/mp4"
        alt={altText}
        data-testid="video-hero-image-source"
      />
    ];

  return (
    <div
      className={containerClasses}
      data-testid="video-hero-image-container"
    >
      <video
        key={videoId}
        className={videoClasses}
        aria-label={altText}
        autoPlay
        onEnded={handleVideoEnded}
        playsInline
        muted
        poster={isMobile ? mobilePosterImageUrl : desktopPosterImageUrl}
        loop={shouldLoopVideo}
        ref={videoRef}
      >
        {sources}
      </video>
      <VideoPlayButton isPlaying={isPlaying} shouldShowReplay={showReplay} handlePlayChange={handlePlayPause} />
    </div>
  );
});

VideoHeroImage.propTypes = {
  imageUrl: string,
  altText: string,
  fullWidth: bool,
  brandHero: bool,
  videoClasses: string,
  isBentoHero: bool,
  desktopVideoUrl: string,
  mobileVideoUrl: string,
  mobilePosterImageUrl: string,
  desktopPosterImageUrl: string,
  shouldLoopVideo: bool,
  videoId: string,
};

VideoHeroImage.displayName = 'VideoHeroImage';

export { VideoHeroImage };